// Meta Pixel Event Tracking Utility
// Fires both Meta and TikTok pixels together so each event is sent once (avoids TikTok duplication).
import * as tiktok from './tiktokPixel';

// Declare fbq function for TypeScript
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
  }
}

export const trackMetaEvent = (
  eventName: string,
  parameters?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('track', eventName, parameters);
      console.log(`[Meta Pixel] Tracked: ${eventName}`, parameters);
    } catch (error) {
      console.error('[Meta Pixel] Error tracking event:', error);
    }
  }
};

// Standard E-commerce Events (each fires Meta + TikTok once)
export const trackViewContent = (product: {
  id: string;
  title: string;
  price: number;
  category?: string;
}) => {
  trackMetaEvent('ViewContent', {
    content_ids: [product.id],
    content_name: product.title,
    content_type: 'product',
    value: product.price,
    currency: 'EGP',
    content_category: product.category || '',
  });
  tiktok.trackViewContent(product);
};

export const trackAddToCart = (product: {
  id: string;
  title: string;
  price: number;
  quantity?: number;
  variantId?: string;
}) => {
  trackMetaEvent('AddToCart', {
    content_ids: [product.id],
    content_name: product.title,
    content_type: 'product',
    value: product.price * (product.quantity || 1),
    currency: 'EGP',
    num_items: product.quantity || 1,
  });
  tiktok.trackAddToCart(product);
};

export const trackInitiateCheckout = (cart: {
  items: Array<{ productId: string; quantity: number }>;
  subtotal: number;
}) => {
  trackMetaEvent('InitiateCheckout', {
    content_ids: cart.items.map(item => item.productId),
    content_type: 'product',
    value: cart.subtotal,
    currency: 'EGP',
    num_items: cart.items.reduce((sum, item) => sum + item.quantity, 0),
  });
  tiktok.trackInitiateCheckout(cart);
};

const META_PURCHASE_KEY_PREFIX = 'meta_purchase_';
const TT_COMPLETE_PAYMENT_KEY_PREFIX = 'tt_complete_payment_';

export type PurchaseTrackingPayload = {
  orderNumber: string;
  total: number;
  items: Array<{ productId: string; quantity: number }>;
};

/** Build pixel payload from POST /orders or GET /orders/:number API body (camelCase or PascalCase). */
export function purchasePayloadFromApiOrder(data: unknown): PurchaseTrackingPayload | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data as Record<string, unknown>;
  const orderNumber = raw.orderNumber ?? raw.OrderNumber;
  if (orderNumber == null || String(orderNumber).trim() === '') return null;

  const itemsRaw = (raw.items ?? raw.Items) as Array<Record<string, unknown>> | undefined;
  const items = (itemsRaw ?? [])
    .map((item) => ({
      productId: String(item.productId ?? item.ProductId ?? ''),
      quantity: Number(item.quantity ?? item.Quantity ?? 1),
    }))
    .filter((item) => item.productId !== '');

  const total = Number(raw.totalAmount ?? raw.TotalAmount ?? 0);

  return {
    orderNumber: String(orderNumber),
    total,
    items,
  };
}

const trackMetaPurchase = (order: PurchaseTrackingPayload) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      const parameters = {
        content_ids: order.items.map((item) => item.productId),
        content_type: 'product',
        value: order.total,
        currency: 'EGP',
        num_items: order.items.reduce((sum, item) => sum + item.quantity, 0),
      };
      // eventID dedupes with Meta CAPI Purchase on the server (same order number)
      window.fbq('track', 'Purchase', parameters, { eventID: order.orderNumber });
      console.log('[Meta Pixel] Tracked: Purchase', parameters, { eventID: order.orderNumber });
    } catch (error) {
      console.error('[Meta Pixel] Error tracking Purchase:', error);
    }
  }
};

export const trackPurchase = (order: PurchaseTrackingPayload) => {
  // Meta: at most once per order per tab (matches server CAPI eventId)
  if (typeof sessionStorage !== 'undefined') {
    const metaKey = `${META_PURCHASE_KEY_PREFIX}${order.orderNumber}`;
    if (!sessionStorage.getItem(metaKey)) {
      sessionStorage.setItem(metaKey, '1');
      trackMetaPurchase(order);
    }
  } else {
    trackMetaPurchase(order);
  }

  // TikTok: at most once per order per tab
  if (typeof sessionStorage !== 'undefined') {
    const ttKey = `${TT_COMPLETE_PAYMENT_KEY_PREFIX}${order.orderNumber}`;
    if (!sessionStorage.getItem(ttKey)) {
      sessionStorage.setItem(ttKey, '1');
      tiktok.trackCompletePayment(order);
    }
  } else {
    tiktok.trackCompletePayment(order);
  }
};

export const trackAddToWishlist = (product: {
  id: string;
  title: string;
  price: number;
}) => {
  trackMetaEvent('AddToWishlist', {
    content_ids: [product.id],
    content_name: product.title,
    content_type: 'product',
    value: product.price,
    currency: 'EGP',
  });
  tiktok.trackAddToWishlist(product);
};

export const trackSearch = (searchQuery: string) => {
  trackMetaEvent('Search', {
    search_string: searchQuery,
  });
};

export const trackAddPaymentInfo = (cart: {
  items: Array<{ productId: string; quantity: number }>;
  subtotal: number;
}) => {
  trackMetaEvent('AddPaymentInfo', {
    content_ids: cart.items.map(item => item.productId),
    content_type: 'product',
    value: cart.subtotal,
    currency: 'EGP',
    num_items: cart.items.reduce((sum, item) => sum + item.quantity, 0),
  });
  tiktok.trackInitiateCheckout(cart);
};
